import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useCreateSimulationMutation } from '@/api/v1';
import { useCustomer } from '@/hooks/useCustomer';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { isCurrencyCodeType } from '@/types/currency.ext';
import { NotNullable } from '@/types/notNullable';
import {
  CustomFees,
  NfTransactionDetailsResponse,
  SimulationRequest,
  SimulationType,
} from '@/types/simulation';
import { isAbortError } from '@/utility/isAbortError';
import { runWithTimer } from '@/utility/runWithTimer';

export interface Simulation {
  readonly id: string | null;
  readonly transactionDetails: NfTransactionDetailsResponse;
  readonly to: Amount;
  readonly from: Amount;
  readonly expiresAt: string | undefined;
}

export interface UseSimulationParameters {
  readonly fromAmount: number | null;
  readonly toAmount: number | null;
  readonly simulationId: string | null;
  readonly fromCurrency: string;
  readonly toCurrency: string;
  readonly fundingMethod: PaymentRailType | null;
  readonly paymentRail?: PaymentRailType;
  readonly destination?: string;
  readonly beneficiaryId?: string;
  readonly payorId?: string;
  readonly customFees?: CustomFees;
}

export const useSimulation = (parameters: UseSimulationParameters): [Simulation, boolean] => {
  const toast = useToast();
  const tr = useTranslator();
  const customer = useCustomer();

  const {
    fromAmount,
    toAmount,
    fromCurrency,
    toCurrency,
    fundingMethod,
    paymentRail,
    customFees,
    destination: paramDestination,
    beneficiaryId: paramBeneficiaryId,
    payorId,
  } = parameters;

  const [createSimulation, createSimulationMutation] = useCreateSimulationMutation();
  const [currentSimulation, setCurrentSimulation] = React.useState<Simulation>(
    (): Simulation => createEmptySimulation(fromCurrency, toCurrency),
  );

  /**
   * FIXME: this is not responsibility of the hook, the hook should probably just
   *        somehow return and/or signal the error, but not show a toast.
   */
  const handleSimulationError = React.useCallback(
    (error: unknown): void => {
      if (isAbortError(error)) {
        return;
      }
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
      console.warn(error);
    },
    [toast, tr],
  );

  React.useEffect((): void => {
    switch (createSimulationMutation.status) {
      case QueryStatus.pending:
      case QueryStatus.uninitialized:
        // Intentionally do nothing
        break;
      case QueryStatus.fulfilled:
        setCurrentSimulation(createSimulationMutation.data);
        createSimulationMutation.reset();
        break;
      case QueryStatus.rejected:
        handleSimulationError(createSimulationMutation.error);
        createSimulationMutation.reset();
        break;
    }
  }, [createSimulationMutation, handleSimulationError, toast, tr]);

  const expired = React.useMemo(
    (): boolean => isSimulationExpired(currentSimulation),
    [currentSimulation],
  );

  const destination = paramDestination || undefined;

  React.useEffect((): void => {
    if (fromAmount === null && toAmount === null) {
      setCurrentSimulation(createEmptySimulation(fromCurrency, toCurrency));
    }
  }, [createSimulationMutation, fromAmount, toAmount, fromCurrency, toCurrency]);

  React.useEffect((): VoidFunction | void => {
    const beneficiaryId = paramBeneficiaryId ?? customer.beneficiaryId;
    if (!beneficiaryId) {
      return;
    }

    if (!isCurrencyCodeType(fromCurrency) || !isCurrencyCodeType(toCurrency)) {
      return;
    }

    if (fundingMethod === PaymentRailType.unset) {
      return;
    }

    if (fromAmount !== null && toAmount !== null) {
      return;
    }

    const amountToSimulate = fromAmount !== null ? fromAmount : toAmount;
    if (amountToSimulate === null) {
      return;
    }

    if (isZero(amountToSimulate)) {
      return;
    }

    const [cancel, abort] = runWithTimer((): VoidFunction => {
      const query = createSimulation({
        type: computeSimulationType(toCurrency),
        beneficiaryId: beneficiaryId,
        from: {
          value: fromAmount ?? 0,
          currencyCode: fromCurrency,
        },
        to: {
          value: toAmount ?? 0,
          currencyCode: toCurrency,
        },
        toCurrencyCode: toCurrency,
        dashboardExperience: true,
        fundingMethod: fundingMethod === null ? null : nullifyCalizaWallet(fundingMethod),
        payorId: payorId,
        ...(destination !== undefined && { destination }),
        ...(paymentRail !== undefined && { paymentRail }),
        ...(customFees !== undefined && { customFees: customFees }),
      } satisfies SimulationRequest);

      return (): void => {
        query.abort();
      };
    }, 300);

    return (): void => {
      cancel();
      abort();
    };
  }, [
    paramBeneficiaryId,
    payorId,
    customer.beneficiaryId,
    createSimulation,
    expired,
    fromAmount,
    fromCurrency,
    fundingMethod,
    paymentRail,
    toCurrency,
    destination,
    toAmount,
    customFees,
  ]);

  return [currentSimulation, createSimulationMutation.status === QueryStatus.pending];
};

const emptyAmount: NotNullable<Amount> = {
  value: 0,
  currencyCode: CurrencyCodeType.UNSET,
};

const emptyTransactionDetails: NfTransactionDetailsResponse = {
  conversionDetails: {
    clientRate: 0,
    effectiveTransactionValue: 0,
    marketExchangeRate: 0,
    calizaRate: 0,
    amountToBeConverted: emptyAmount,
    integratorRate: 0,
  },
  feeDetails: {
    totalFees: emptyAmount,
    integratorFee: emptyAmount,
    taxes: emptyAmount,
    fixedFees: [],
    oldFees: [],
    percentageFees: [],
    spreadFees: [],
    vet: '0.0000',
  },
  expectedSettlementTimeDetails: null,
};

const createEmptySimulation = (fromCurrency: string, toCurrency: string): Simulation => ({
  id: null,
  transactionDetails: emptyTransactionDetails,
  to: {
    value: null,
    currencyCode: isCurrencyCodeType(toCurrency) ? toCurrency : CurrencyCodeType.UNSET,
  },
  from: {
    value: null,
    currencyCode: isCurrencyCodeType(fromCurrency) ? fromCurrency : CurrencyCodeType.UNSET,
  },
  expiresAt: undefined,
});

// TODO: implement this function
const isSimulationExpired = (_simulation: Simulation): boolean => false;

const computeSimulationType = (targetCurrency: CurrencyCodeType): SimulationType => {
  switch (targetCurrency) {
    case CurrencyCodeType.USDC:
    case CurrencyCodeType.USDT:
      return SimulationType.CRYPTO;
    case CurrencyCodeType.USD:
    case CurrencyCodeType.BRL:
    case CurrencyCodeType.MXN:
    case CurrencyCodeType.COP:
    case CurrencyCodeType.ARS:
    case CurrencyCodeType.HKD:
    case CurrencyCodeType.SGD:
      return SimulationType.FIAT;
    case CurrencyCodeType.UNSET:
    case CurrencyCodeType.ANY:
    default:
      return SimulationType.NONE;
  }
};

export const nullifyCalizaWallet = (method: PaymentRailType): PaymentRailType | null =>
  method === PaymentRailType.CalizaWallet ? null : method;

const isZero = (value: number | string): boolean => {
  if (typeof value !== 'number') {
    console.warn(
      'oh no! we are trying to determine if a number is zero, but it is not even a number',
    );
    return true;
  }

  return value.toFixed(2) === '0.00';
};
